<template>
  <div class="dialog-shortlisting">
    <WdsButton v-if="!isShortlisted" :disabled="loading" tertiary @click="addToShortlist">
      <WdsStack align-center gap-sm>
        <WdsIcon name="pin" :size="18" />
        {{ $t('Shortlist') }}
        <transition name="fade">
          <!-- eslint-disable-next-line vue/no-undef-components -- Baseline eslint ignore please fix if working on this-->
          <button-spinner v-if="loading" />
        </transition>
      </WdsStack>
    </WdsButton>
    <WdsButton v-else :disabled="loading" secondary @click="removeFromShortlist">
      <WdsStack align-center gap-sm>
        <WdsIcon name="pin-filled" :size="18" />
        {{ $t('Shortlisted') }}
        <transition name="fade">
          <!-- eslint-disable-next-line vue/no-undef-components -- Baseline eslint ignore please fix if working on this-->
          <button-spinner v-if="loading" />
        </transition>
      </WdsStack>
    </WdsButton>
  </div>
</template>

<script>
import gql from 'graphql-tag'
import { WdsButton, WdsIcon, WdsStack } from '@worksome/wds-components'
import { BASE_FRAGMENT_FOR_DIALOG_MUTATIONS } from '@/state/apollo/fragments'
import { NetworkError, Message } from '@/utils/notification'

/**
 * @emits added-to-shortlist The shortlisting was successful
 * @emits removed-from-shortlist The unshortlisting was successful
 */
export default {
  name: 'DialogShortlistingButton',
  components: {
    WdsButton,
    WdsIcon,
    WdsStack,
  },
  props: {
    dialogLocalId: { type: Number, required: true },
    accountGlobalId: { type: String, required: true },
    yjobLocalId: { type: Number, required: true },
    shortlistLocalId: {
      default: () => null,
      validator: (prop) => typeof prop === 'number' || prop === null,
    },
  },
  data() {
    return {
      loading: false,
    }
  },
  computed: {
    isShortlisted() {
      return this.shortlistLocalId != null
    },
    createShortlistInput() {
      return {
        yjobId: this.yjobLocalId,
        accountId: this.accountGlobalId,
        dialogId: this.dialogLocalId,
      }
    },
  },
  methods: {
    async addToShortlist() {
      if (this.loading) {
        return
      }
      this.loading = true
      await this.$apollo
        .mutate({
          mutation: gql`
            mutation CreateShortlist($input: CreateShortlistInput!, $yjobId: Int!) {
              createShortlist(input: $input) {
                globalId
                account {
                  globalId
                  name
                }
                dialog {
                  ...BaseFragmentForDialogMutations
                  shortlisted(yjobId: $yjobId) {
                    data {
                      localId
                    }
                    paginatorInfo {
                      total
                    }
                  }
                  dialogType
                }
              }
            }
            ${BASE_FRAGMENT_FOR_DIALOG_MUTATIONS}
          `,
          variables: {
            input: this.createShortlistInput,
            yjobId: this.yjobLocalId,
          },
        })
        .then(({ data }) => {
          new Message(
            $t('Moved :name to shortlisted conversations', {
              name: data.createShortlist?.account?.name,
            }),
          ).dispatch(this)
          this.$emit('added-to-shortlist')
        })
        .catch((e) => {
          new NetworkError({
            msg: this.$t('Error occurred. The action was not completed'),
            ex: e,
          }).dispatch(this)
        })
        .finally(() => {
          this.loading = false
        })
    },
    async removeFromShortlist() {
      if (this.loading) {
        return
      }
      this.loading = true
      await this.$apollo
        .mutate({
          mutation: gql`
            mutation DeleteShortlist($localId: Int!, $yjobId: Int!) {
              deleteShortlist(localId: $localId) {
                globalId
                account {
                  globalId
                  name
                }
                dialog {
                  ...BaseFragmentForDialogMutations
                  shortlisted(yjobId: $yjobId) {
                    data {
                      localId
                    }
                    paginatorInfo {
                      total
                    }
                  }
                  dialogType
                  freelancer {
                    globalId
                    name
                  }
                }
              }
            }
            ${BASE_FRAGMENT_FOR_DIALOG_MUTATIONS}
          `,
          variables: {
            localId: this.shortlistLocalId ?? null,
            yjobId: this.yjobLocalId,
          },
        })
        .then(({ data }) => {
          new Message(
            $t('Moved :name to new conversations', {
              name: data.deleteShortlist.account?.name,
            }),
          ).dispatch(this)
          this.$emit('removed-from-shortlist')
        })
        .catch((e) => {
          new NetworkError({
            msg: this.$t('Error occurred. The action was not completed'),
            ex: e,
          }).dispatch(this)
        })
        .finally(() => {
          this.loading = false
        })
    },
  },
}
</script>
